import { useState } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import {
  Shield,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Layers,
} from "lucide-react";
import { apiService } from "../services/api";
import {
  DtVersionSelection,
  LinkedDtProject,
  ProductFindingsResult,
  VulnerabilityFinding,
} from "../types";
import { dependencyTrackProjectUrl } from "../utils/env";
import { describeDtVersions } from "../utils/dtVersions";
import { useDtVersionSelection } from "../hooks/useDtVersionSelection";
import DtVersionSelectModal from "./DtVersionSelectModal";

interface ProductDependencyTrackPanelProps {
  productId: string;
}

const SEVERITY_STYLES: Record<string, string> = {
  CRITICAL:
    "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800",
  HIGH: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 border-orange-200 dark:border-orange-800",
  MEDIUM:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  LOW: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800",
  INFO: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600",
  UNASSIGNED:
    "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600",
};

function severityClass(sev: string): string {
  return (
    SEVERITY_STYLES[sev?.toUpperCase()] ?? SEVERITY_STYLES.UNASSIGNED ?? ""
  );
}

/** Build a deep-link URL for a vulnerability based on its source. */
function vulnUrl(vulnId: string, source?: string): string {
  const s = source?.toUpperCase();
  if (s === "GITHUB")
    return `https://github.com/advisories/${encodeURIComponent(vulnId)}`;
  if (s === "OSSINDEX")
    return `https://ossindex.sonatype.org/vulnerability/${encodeURIComponent(vulnId)}`;
  // NVD is the default — works for any CVE-* identifier.
  return `https://nvd.nist.gov/vuln/detail/${encodeURIComponent(vulnId)}`;
}

const FINDINGS_PAGE_SIZE = 10;

export default function ProductDependencyTrackPanel({
  productId,
}: ProductDependencyTrackPanelProps) {
  const [showFindings, setShowFindings] = useState(true);
  const [findingsPage, setFindingsPage] = useState(1);
  const [activeSeverities, setActiveSeverities] = useState<string[]>([
    "CRITICAL",
    "HIGH",
  ]);
  const [versionSelection, setVersionSelection] = useDtVersionSelection();
  const [showVersionModal, setShowVersionModal] = useState(false);

  const toggleSeverity = (sev: string) => {
    setActiveSeverities((prev) =>
      prev.includes(sev) ? prev.filter((s) => s !== sev) : [...prev, sev],
    );
    setFindingsPage(1);
  };

  const applyVersions = (selection: DtVersionSelection) => {
    setVersionSelection(selection);
    setFindingsPage(1);
  };

  const findingsKeyBase = ["product-dt-findings", productId];

  const { data: projectsRes, isLoading: projectsLoading } = useQuery(
    ["product-dt-projects", productId],
    () => apiService.getProductDependencyTrackProjects(productId),
    { enabled: !!productId },
  );
  const projects: LinkedDtProject[] = (projectsRes?.data as any)?.data ?? [];

  const {
    data: findingsRes,
    isLoading: findingsLoading,
    isFetching: findingsFetching,
    refetch: refetchFindings,
  } = useQuery(
    [...findingsKeyBase, findingsPage, activeSeverities, versionSelection],
    () =>
      apiService.getProductDependencyTrackFindings(
        productId,
        findingsPage,
        FINDINGS_PAGE_SIZE,
        activeSeverities.length > 0 ? activeSeverities : undefined,
        versionSelection,
      ),
    {
      enabled: !!productId,
      keepPreviousData: true,
    },
  );

  const findings: ProductFindingsResult | null =
    (findingsRes?.data as any)?.data ?? null;

  const pageFindings: VulnerabilityFinding[] = findings?.findings ?? [];
  const projectErrors = findings?.errors ?? [];
  const totalPages = findings?.totalPages ?? 1;
  const totalFindings = findings?.total ?? 0;
  const pageStart =
    totalFindings === 0 ? 0 : (findingsPage - 1) * FINDINGS_PAGE_SIZE + 1;
  const pageEnd = Math.min(findingsPage * FINDINGS_PAGE_SIZE, totalFindings);
  // `total` is post-filter; the severity counts are not, so use them for visibility.
  const allFindingsCount = findings
    ? findings.critical +
      findings.high +
      findings.medium +
      findings.low +
      findings.unassigned
    : 0;

  // No Dependency-Track projects linked to any of the product's applications.
  if (!projectsLoading && projects.length === 0) {
    return null;
  }

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg">
      <div className="px-6 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setShowFindings((v) => !v)}
          className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100"
        >
          {showFindings ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          <Shield className="h-4 w-4" />
          Vulnerabilities (All Applications)
          {findings && (
            <span className="text-xs font-normal text-gray-500">
              ({allFindingsCount})
            </span>
          )}
        </button>
        <div className="flex items-center gap-3">
          {projects.length > 0 && (
            <button
              type="button"
              onClick={() => setShowVersionModal(true)}
              className="btn btn-secondary text-xs px-2 py-1 flex items-center gap-1"
              title="Choose the version shown for each project"
            >
              <Layers className="h-3 w-3" />
              {describeDtVersions(versionSelection, projects)}
            </button>
          )}
          {findings && (
            <div className="flex items-center gap-1.5">
              {(
                [
                  ["CRITICAL", findings.critical],
                  ["HIGH", findings.high],
                  ["MEDIUM", findings.medium],
                  ["LOW", findings.low],
                ] as const
              )
                .filter(([, n]) => n > 0)
                .map(([sev, n]) => (
                  <button
                    type="button"
                    key={sev}
                    onClick={() => toggleSeverity(sev)}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium transition-opacity ${severityClass(sev)} ${activeSeverities.length > 0 && !activeSeverities.includes(sev) ? "opacity-40" : ""}`}
                    title={
                      activeSeverities.includes(sev)
                        ? `Hide ${sev.toLowerCase()} findings`
                        : `Show ${sev.toLowerCase()} findings`
                    }
                  >
                    {n} {sev.charAt(0) + sev.slice(1).toLowerCase()}
                  </button>
                ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => refetchFindings()}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Refresh findings"
          >
            <RefreshCw
              className={`h-4 w-4 ${findingsFetching ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {showFindings && (
        <div className="px-6 pb-4">
          {projectErrors.length > 0 && (
            <div className="mb-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Could not load findings for{" "}
                {projectErrors.map((p) => p.dtProjectName).join(", ")}.
              </span>
            </div>
          )}

          {findingsLoading && !findings ? (
            <div className="py-6 flex justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
            </div>
          ) : pageFindings.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">
              {projectErrors.length > 0
                ? "No findings could be loaded."
                : allFindingsCount > 0
                  ? "No findings match the selected severity."
                  : "No vulnerabilities found across the linked applications."}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                      <th className="py-2 pr-4 font-medium">Application</th>
                      <th className="py-2 pr-4 font-medium">
                        Dependency-Track Project
                      </th>
                      <th className="py-2 pr-4 font-medium">Version</th>
                      <th className="py-2 pr-4 font-medium">Vulnerability</th>
                      <th className="py-2 pr-4 font-medium">Severity</th>
                      <th className="py-2 pr-4 font-medium">CVSS</th>
                      <th className="py-2 pr-4 font-medium">Component</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {pageFindings.map((finding, idx) => (
                      <tr
                        key={`${finding.vulnId}-${finding.componentPurl}-${idx}`}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <td className="py-2 pr-4">
                          {finding.applicationId ? (
                            <Link
                              to={`/applications/${finding.applicationId}`}
                              className="text-xs font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:underline"
                            >
                              {finding.applicationName || finding.applicationId}
                            </Link>
                          ) : (
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {finding.applicationName || "—"}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4">
                          {finding.projectUuid ? (
                            <a
                              href={dependencyTrackProjectUrl(
                                finding.projectUuid,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:underline"
                            >
                              {finding.projectName || finding.projectUuid}
                            </a>
                          ) : (
                            <span className="text-xs text-gray-700 dark:text-gray-300">
                              {finding.projectName || "—"}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4 font-mono text-xs text-gray-700 dark:text-gray-300">
                          {finding.projectVersion || "—"}
                        </td>
                        <td className="py-2 pr-4">
                          <a
                            href={vulnUrl(finding.vulnId, finding.source)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:underline"
                          >
                            {finding.vulnId}
                          </a>
                          {finding.source && (
                            <span className="ml-1 text-xs text-gray-400">
                              {finding.source}
                            </span>
                          )}
                          {finding.title && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">
                              {finding.title}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${severityClass(finding.severity)}`}
                          >
                            {finding.severity || "UNKNOWN"}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                          {finding.cvssScore > 0
                            ? finding.cvssScore.toFixed(1)
                            : "—"}
                        </td>
                        <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                          <span className="font-mono text-xs">
                            {finding.componentName}
                            {finding.componentVersion
                              ? `@${finding.componentVersion}`
                              : ""}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>
                  Showing {pageStart}–{pageEnd} of {totalFindings}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFindingsPage((p) => Math.max(1, p - 1))}
                    disabled={findingsPage <= 1 || findingsFetching}
                    className="btn btn-secondary px-2 py-1 flex items-center gap-1 disabled:opacity-40"
                    title="Previous page"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" /> Prev
                  </button>
                  <span className="tabular-nums">
                    Page {findingsPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setFindingsPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={findingsPage >= totalPages || findingsFetching}
                    className="btn btn-secondary px-2 py-1 flex items-center gap-1 disabled:opacity-40"
                    title="Next page"
                  >
                    Next <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {showVersionModal && (
        <DtVersionSelectModal
          projects={projects}
          selection={versionSelection}
          onApply={applyVersions}
          onClose={() => setShowVersionModal(false)}
        />
      )}
    </div>
  );
}
